import type { FileSaveOptions, FileSaveResult, FileLoadResult } from './types'

export class TauriFileService {
  private tauri: any

  constructor() {
    if (typeof window === 'undefined' || !(window as any).__TAURI__) {
      throw new Error('Tauri API not available')
    }
    this.tauri = (window as any).__TAURI__
  }

  async save(content: any, options: FileSaveOptions = {}): Promise<FileSaveResult> {
    const { dialog, fs } = this.tauri

    // 获取保存路径
    const filePath = await dialog.save({
      defaultPath: options.defaultName || 'untitled.json',
      filters: options.filters || [{ name: 'JSON', extensions: ['json'] }]
    })

    if (!filePath) {
      return { success: false, cancelled: true, platform: 'tauri' }
    }

    // 保存文件
    await fs.writeTextFile({
      path: filePath,
      contents: JSON.stringify(content, null, 2)
    })

    // 记录最近文件
    await this.addToRecentFiles(filePath)

    return {
      success: true,
      path: filePath,
      platform: 'tauri'
    }
  }

  async load(filePath?: string): Promise<FileLoadResult> {
    const { dialog, fs } = this.tauri

    let targetPath = filePath

    // 如果没有提供路径，打开文件选择对话框
    if (!targetPath) {
      const selected = await dialog.open({
        multiple: false,
        filters: [
          { name: 'JSON', extensions: ['json'] },
          { name: 'Canvas Project', extensions: ['canvas'] },
          { name: 'All Files', extensions: ['*'] }
        ]
      })

      if (!selected || (Array.isArray(selected) && selected.length === 0)) {
        throw new Error('No file selected')
      }

      targetPath = Array.isArray(selected) ? selected[0] : (selected as string)
    }

    if (!targetPath) {
      throw new Error('No file path provided')
    }

    const content = await fs.readTextFile(targetPath)
    const parsed = JSON.parse(content)

    // 记录最近文件
    await this.addToRecentFiles(targetPath)

    return {
      content: parsed,
      path: targetPath,
      name: targetPath.split(/[/\\]/).pop() || 'untitled'
    }
  }

  private async addToRecentFiles(filePath: string): Promise<void> {
    try {
      const { path, fs } = this.tauri
      const configDir = await path.appConfigDir()
      const recentFile = `${configDir}/recent-files.json`

      let recentFiles: string[] = []
      try {
        const content = await fs.readTextFile(recentFile)
        recentFiles = JSON.parse(content)
      } catch {
        // 文件不存在，使用空数组
      }

      // 移除重复项，添加到开头
      recentFiles = recentFiles.filter((p: string) => p !== filePath)
      recentFiles.unshift(filePath)

      // 只保留最近20个
      recentFiles = recentFiles.slice(0, 20)

      // 确保目录存在
      await fs.createDir(configDir, { recursive: true })

      // 保存
      await fs.writeTextFile({
        path: recentFile,
        contents: JSON.stringify(recentFiles, null, 2)
      })
    } catch (error) {
      console.warn('Failed to save recent files:', error)
    }
  }

  async getRecentFiles(): Promise<string[]> {
    try {
      const { path, fs } = this.tauri
      const configDir = await path.appConfigDir()
      const recentFile = `${configDir}/recent-files.json`

      const content = await fs.readTextFile(recentFile)
      return JSON.parse(content)
    } catch {
      return []
    }
  }
}

export class TauriNotificationService {
  private tauri: any

  constructor() {
    if (typeof window === 'undefined' || !(window as any).__TAURI__) {
      throw new Error('Tauri API not available')
    }
    this.tauri = (window as any).__TAURI__
  }

  async show(title: string, body: string): Promise<void> {
    const { notification } = this.tauri
    await notification.sendNotification({
      title,
      body
    })
  }
}

